import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { errorMessage } from '@/lib/errors';
import { financeExpectedByApartmentQuery, financeRulesQuery } from '@/lib/finance';
import { formatMinor } from '@/lib/money';
import { FINANCE_EXPECTED_STATUS_TONE } from '@/lib/status-tone';

const TYPE_LABEL: Record<string, string> = {
  recurring: 'Recurring',
  per_stay: 'Per stay',
  commission_invoice: 'Commission invoice',
};

/**
 * "Finance" tab on the apartment page (spec §14) — this apartment's own
 * rules (recurring/per_stay scoped to it — commission_invoice is always
 * company-scoped and never appears here) and its expected payments, both
 * cross-linked into /finance/rules/$id and /finance/expected/$id. Mounted
 * only once ApartmentDetail's own TAB_PERMISSIONS.finance.read gate has
 * already passed — this component does not re-check the permission itself,
 * same as every other apartment tab section (OverviewSection, ApartmentPhotos, …).
 */
export function ApartmentFinanceSection({ apartmentID }: { apartmentID: string }) {
  const rules = useQuery(financeRulesQuery({ apartmentId: apartmentID, includeDisabled: true }));
  const expected = useQuery(financeExpectedByApartmentQuery(apartmentID));

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-[0.06em] text-muted">Rules</h3>
        {rules.isLoading ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : rules.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage(rules.error)}
          </p>
        ) : (rules.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-muted">No rules scoped to this apartment.</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {(rules.data?.items ?? []).map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 text-sm">
                <Link to="/finance/rules/$id" params={{ id: r.id }} className="text-primary hover:underline">
                  {r.name}
                </Link>
                <span className="text-muted">{TYPE_LABEL[r.type] ?? r.type}</span>
                <Badge variant={r.enabled ? 'success' : 'neutral'} dot>
                  {r.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-[0.06em] text-muted">Expected payments</h3>
        {expected.isLoading ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : expected.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage(expected.error)}
          </p>
        ) : (expected.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-muted">No expected payments recorded against this apartment.</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {(expected.data?.items ?? []).map((e) => {
              const tone = FINANCE_EXPECTED_STATUS_TONE[e.status];
              return (
                <li key={e.id} className="flex items-center justify-between gap-3 text-sm">
                  <Link to="/finance/expected/$id" params={{ id: e.id }} className="text-primary hover:underline">
                    {e.due_date}
                  </Link>
                  <span className="tabular-nums text-muted">{formatMinor(e.amount_minor, e.currency)}</span>
                  <Badge variant={tone.variant} dot>
                    {tone.label}
                  </Badge>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
